import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'

import CTA from '../../components/cms/cta'
import Copy from '../../components/cms/copy'
import Image from '../../components/cms/image'
import ModuleContainer from '../../components/module-container'
import {hasValidImageSrc, hasValidString} from '../../utils/utils'
import contentTeaserRecipe, {CONTENT_INFO_VARIANTS} from './index.recipe'

/**
 * Renders teaser copy and optional CTA.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.copy - Copy content.
 * @param {Object} [props.cta] - CTA configuration.
 * @param {string} props.variantKey - Content variant key.
 * @returns {JSX.Element} Copy and CTA content.
 */
const ContentInfo = ({copy, cta, variantKey}) => {
    const {button, copy: copyVariant} = CONTENT_INFO_VARIANTS[variantKey]

    return (
        <>
            <Copy {...copy} variant={copyVariant} alignment="inherit" />
            {hasValidString(cta?.label) ? (
                <CTA {...cta} variant={button} size="sm" alignSelf="flex-start" />
            ) : null}
        </>
    )
}

ContentInfo.propTypes = {
    copy: PropTypes.shape({
        text: PropTypes.string
    }).isRequired,
    variantKey: PropTypes.oneOf(Object.keys(CONTENT_INFO_VARIANTS)).isRequired,
    cta: PropTypes.object
}

/**
 * Content teaser module supporting full and split layouts.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.moduleId] - Module identifier.
 * @param {string} [props.moduleType] - Module type.
 * @param {string} [props.pageId] - Page identifier.
 * @param {string} [props.anchor] - Optional anchor id.
 * @param {Object} [props.image] - Image asset.
 * @param {string} [props.image.src] - Image source URL.
 * @param {string} [props.image.alt] - Image alt text.
 * @param {string} [props.image.title] - Image title.
 * @param {string} [props.image.disBaseLink] - DIS image URL.
 * @param {('full'|'split')} [props.variant] - Teaser layout variant.
 * @param {Object} props.copy - Copy content.
 * @param {Object} [props.cta] - CTA configuration.
 * @param {('40%'|'80%'|'100%')} [props.darken] - Overlay intensity for full-image variant.
 * @returns {JSX.Element} Rendered content teaser module.
 */
const ContentTeaser = ({
    moduleId,
    moduleType,
    pageId,
    anchor,
    image,
    variant = 'full',
    copy,
    cta,
    darken
}) => {
    const hasValidImage = hasValidImageSrc(image)
    const variantKey = `${variant}:${hasValidImage}`

    const recipe = useSlotRecipe({recipe: contentTeaserRecipe})
    const styles = useMemo(
        () => recipe({variant, hasImage: hasValidImage, darken}),
        [recipe, variant, hasValidImage, darken]
    )

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background="none"
            modulePadding="none"
            spacing="none"
        >
            <Box css={styles.root}>
                {hasValidImage ? (
                    <Box css={styles.imageWrapper}>
                        <Image
                            widths={{base: '85vw', s: '50vw', l: '37.5vw'}}
                            src={image.src}
                            alt={image.alt}
                            title={image.title}
                            {...styles.image}
                        />
                    </Box>
                ) : null}

                <Box css={styles.content}>
                    <ContentInfo copy={copy} cta={cta} variantKey={variantKey} />
                </Box>
            </Box>
        </ModuleContainer>
    )
}

ContentTeaser.propTypes = {
    anchor: PropTypes.string,
    image: PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        title: PropTypes.string,
        disBaseLink: PropTypes.string
    }),
    copy: PropTypes.shape({
        text: PropTypes.string
    }).isRequired,
    cta: PropTypes.object,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    darken: PropTypes.oneOf(['0%', '40%', '80%', '100%']),
    variant: PropTypes.oneOf(['full', 'split'])
}

export default ContentTeaser
